import React, { createContext, useContext, useReducer } from "react";

// Create context
const FormContext = createContext();

// Action types
const SET_FIELDS = "SET_FIELDS";
const SET_VALUES = "SET_VALUES";
const UPDATE_VALUE = "UPDATE_VALUE";
const RESET_FORM = "RESET_FORM";
const SET_ERRORS = "SET_ERRORS";
const CLEAR_ERRORS = "CLEAR_ERRORS";
const SET_SUBMITTING = "SET_SUBMITTING";
const SET_RESPONSE = "SET_RESPONSE";
const SET_STEP = "SET_STEP";

// Initial state
const initialState = {
  fields: [],
  values: {},
  errors: {},
  submitting: false,
  response: null,
  step: 0,
};

// Reducer function
const formReducer = (state, action) => {
  switch (action.type) {
    case SET_FIELDS:
      return {
        ...state,
        fields: action.payload,
        // Set default values from fields
        values: action.payload.reduce(
          (values, field) => ({
            ...values,
            [field.name]:
              field.type === "checkbox"
                ? !!parseInt(field.defaultValue || "0")
                : field.type === "mcq"
                ? []
                : field.defaultValue || "",
          }),
          {}
        ),
      };

    case SET_VALUES:
      return {
        ...state,
        values: action.payload,
      };

    case UPDATE_VALUE:
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: action.payload.value,
        },
      };

    case RESET_FORM:
      return {
        ...initialState,
        fields: state.fields,
        values: state.fields.reduce(
          (values, field) => ({
            ...values,
            [field.name]:
              field.type === "checkbox"
                ? !!parseInt(field.defaultValue || "0")
                : field.type === "mcq"
                ? []
                : field.defaultValue || "",
          }),
          {}
        ),
      };

    case SET_ERRORS:
      return {
        ...state,
        errors: action.payload,
      };

    case CLEAR_ERRORS:
      return {
        ...state,
        errors: {},
      };

    case SET_SUBMITTING:
      return {
        ...state,
        submitting: action.payload,
      };

    case SET_RESPONSE:
      return {
        ...state,
        response: action.payload,
        submitting: false,
      };

    case SET_STEP:
      return {
        ...state,
        step: action.payload,
      };

    default:
      return state;
  }
};

/**
 * Provider component for form state management
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const FormProvider = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, initialState);

  // Actions
  const setFields = (fields) => {
    dispatch({ type: SET_FIELDS, payload: fields });
  };

  const setValues = (values) => {
    dispatch({ type: SET_VALUES, payload: values });
  };

  const updateValue = (name, value) => {
    dispatch({ type: UPDATE_VALUE, payload: { name, value } });
  };

  const resetForm = () => {
    dispatch({ type: RESET_FORM });
  };

  const setErrors = (errors) => {
    dispatch({ type: SET_ERRORS, payload: errors });
  };

  const clearErrors = () => {
    dispatch({ type: CLEAR_ERRORS });
  };

  const setSubmitting = (isSubmitting) => {
    dispatch({ type: SET_SUBMITTING, payload: isSubmitting });
  };

  const setResponse = (response) => {
    dispatch({ type: SET_RESPONSE, payload: response });
  };

  const setStep = (step) => {
    dispatch({ type: SET_STEP, payload: step });
  };

  // Value object
  const value = {
    ...state,
    setFields,
    setValues,
    updateValue,
    resetForm,
    setErrors,
    clearErrors,
    setSubmitting,
    setResponse,
    setStep,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

/**
 * Custom hook to access form context
 *
 * @returns {Object} Form context
 */
export const useForm = () => {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error("useForm must be used within a FormProvider");
  }

  return context;
};

export default FormContext;
